import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable react/forbid-prop-types */

/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { IconDownTriangle, MenuButton, MenuItem } from "../../";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
/**
 * The Select/Unselect All control is a three-state checkbox
 * control that performs bulk selections in a Data Table,
 * List View or a longer set of form options. This control
 * also provides modest visual feedback regarding the state
 * of the selections.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function SelectAll(_ref) {
  let {
    className,
    disabled,
    customStrings,
    handleDeselectAllAcrossPages,
    handleDeselectAllItemsOnPage,
    handleSelectAllAcrossPages,
    handleSelectAllItemsOnPage,
    totalItems,
    totalItemsSelectedAcrossAllPages,
    selectedValue,
    onSelectedValueChange,
    controlMenuButtonFocus
  } = _ref;
  const theme = useTheme();
  const {
    cssRoot,
    disabledMenuItem,
    selectedStateCSS,
    menuItemStyleCSS
  } = extractTheme({
    theme,
    totalItems,
    totalItemsSelectedAcrossAllPages
  });

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  let menuButtonRef = useRef(null);
  const [value, setValue] = useState(selectedValue);
  const [selectedSelectAllOption, setSelectedSelectAllOption] = useState("");
  /**
   * Returns the formatted `aria-label`.
   */

  const getAriaLabel = () => `${componentStrings.selectAllButton} ${totalItemsSelectedAcrossAllPages} ${componentStrings.itemsSelected}`;

  const setMenuButtonRef = node => {
    menuButtonRef = node;
  };

  const handleSelectedValueChange = value => {
    const {
      selectAllOnThisPage,
      selectAllItems,
      unselectAllOnThisPage,
      unselectAllItems
    } = componentStrings;
    let handler;
    /**
     * We need to remove the empty default case in order to get 100% coverage.
     */
    // eslint-disable-next-line default-case

    switch (value) {
      case selectAllOnThisPage:
        setSelectedSelectAllOption(selectAllOnThisPage);
        handler = handleSelectAllItemsOnPage;
        break;

      case selectAllItems:
        setSelectedSelectAllOption(selectAllItems);
        handler = handleSelectAllAcrossPages;
        break;

      case unselectAllOnThisPage:
        setSelectedSelectAllOption(unselectAllOnThisPage);
        handler = handleDeselectAllItemsOnPage;
        break;

      case unselectAllItems:
        setSelectedSelectAllOption(unselectAllItems);
        handler = handleDeselectAllAcrossPages;
        break;
    }
    /**
     * We have a handler for every item, so we're guaranteed that it will be one of the cases from the
     * switch. If we wrap this with a condition, we won't meet coverage (just like above)...
     */


    handler(value);

    if (onSelectedValueChange) {
      onSelectedValueChange(value);
    }
  };

  useEffect(() => {
    setValue(value);
  }, [value]);
  useEffect(() => {
    if (controlMenuButtonFocus && value && value === selectedSelectAllOption) {
      menuButtonRef.focus();
      setSelectedSelectAllOption("");
    }
  }, [selectedSelectAllOption]);
  const {
    currentlySelectedItems,
    itemsSelected,
    selectAllOnThisPage,
    selectAllItems,
    unselectAllOnThisPage,
    unselectAllItems
  } = componentStrings;
  return _jsx("div", {
    css: cssRoot,
    children: _jsx("div", {
      className: className,
      disabled: disabled || null,
      children: _jsxs(MenuButton, {
        label: getAriaLabel(),
        disabled: disabled || null,
        kind: "standard",
        rightIcon: _jsx(IconDownTriangle, {
          width: "11px",
          height: "11px",
          decorative: true
        }),
        selectedValue: value,
        onSelectedValueChange: handleSelectedValueChange,
        disableSelectedState: true,
        noFocusOnMenuClose: controlMenuButtonFocus,
        ref: setMenuButtonRef,
        buttonStyles: selectedStateCSS,
        children: [_jsx(MenuItem, {
          value: currentlySelectedItems,
          disabled: true,
          menuItemStyles: disabledMenuItem,
          children: `${totalItemsSelectedAcrossAllPages} ${itemsSelected}`
        }), _jsx(MenuItem, {
          value: selectAllOnThisPage,
          menuItemStyles: menuItemStyleCSS,
          children: selectAllOnThisPage
        }), _jsx(MenuItem, {
          value: selectAllItems,
          menuItemStyles: menuItemStyleCSS,
          children: `${selectAllItems} (${totalItems || 0})`
        }), _jsx(MenuItem, {
          value: unselectAllOnThisPage,
          menuItemStyles: menuItemStyleCSS,
          children: unselectAllOnThisPage
        }), _jsx(MenuItem, {
          value: unselectAllItems,
          menuItemStyles: menuItemStyleCSS,
          children: `${unselectAllItems} (${totalItemsSelectedAcrossAllPages || 0})`
        })]
      })
    })
  });
}

SelectAll.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string,

  /**
   * If `MenuButton` focus needs to be controlled from SelectAll.
   */
  controlMenuButtonFocus: PropTypes.bool,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * String used for the option to unselect all items.
     * Default: "Unselect all items".
     */
    unselectAllItems: PropTypes.string,

    /**
     * String used for the option to unselect all items on the page.
     * Default: "Unselect all on this page".
     */
    unselectAllOnThisPage: PropTypes.string,

    /**
     * String used in the first option along with count of the selected items.
     * Default: "items selected".
     */
    itemsSelected: PropTypes.string,

    /**
     * String used for the option to select all items.
     * Default: "Select all items".
     */
    selectAllItems: PropTypes.string,

    /**
     * String used for the option to select all items on the page.
     * Default: "Select all on this page".
     */
    selectAllOnThisPage: PropTypes.string,

    /**
     * String used in the `aria-label` of the select all button.
     * Default: "Select All,".
     */
    selectAllButton: PropTypes.string
  }),

  /** Disables the component. */
  disabled: PropTypes.bool,

  /** Callback function to unselect all selected items across all data/pages. */
  handleDeselectAllAcrossPages: PropTypes.func,

  /** Callback function to unselect all items on the current page. */
  handleDeselectAllItemsOnPage: PropTypes.func,

  /** Callback function to select all items across all data/pages. */
  handleSelectAllAcrossPages: PropTypes.func,

  /** Callback function to select all items on the current page. */
  handleSelectAllItemsOnPage: PropTypes.func,

  /**
   * This is a pass-thru prop to the `MenuButton`.
   * See `MenuButton` documentation.
   */
  onSelectedValueChange: PropTypes.func,

  /**
   * This is a pass-thru prop to the `MenuButton`.
   * See `MenuButton` documentation.
   */
  selectedValue: PropTypes.string,

  /** The total items available to be selected across all data/pages. */
  totalItems: PropTypes.number,

  /** The total items selected across all data/pages. */
  totalItemsSelectedAcrossAllPages: PropTypes.number
} : {};
SelectAll.defaultProps = {
  buttonClassName: "",
  buttonStyle: {},
  className: "",
  disabled: false,
  customStrings: null,
  handleDeselectAllAcrossPages: null,
  handleDeselectAllItemsOnPage: null,
  handleSelectAllAcrossPages: null,
  handleSelectAllItemsOnPage: null,
  totalItems: 10,
  totalItemsSelectedAcrossAllPages: 10,
  selectedValue: "",
  onSelectedValueChange: null,
  controlMenuButtonFocus: false
};
export default SelectAll;